import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getClients, getClientById, createClient, updateClient, deleteClient, syncClientsFromAppointments } from '../../services/api';
import { safeParseJSON } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatDate } from '../../utils/formatDateTime';
import PageTour from '../../components/tenant/PageTour';
import PhoneInput from '../../components/PhoneInput';
import { formatPhoneBR } from '../../utils/phone';

const TOUR_STEPS = [
  {
    selector: '[data-tour="clients-sync-btn"]',
    title: 'Sincronizar histórico',
    content: 'Tem agendamentos antigos sem cliente cadastrado? Clique aqui pra popular o CRM automaticamente com quem já passou por você.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="clients-new-btn"]',
    title: 'Cadastro manual',
    content: 'Adicione clientes que você já conhece mas nunca agendaram pelo link — útil pra migrar base antiga.',
    placement: 'bottom',
  },
  {
    selector: '[data-tour="clients-search"]',
    title: 'Buscar cliente',
    content: 'Pesquise por nome, telefone ou e-mail pra encontrar rapidamente.',
    placement: 'bottom',
  },
  {
    title: 'Ver histórico do cliente',
    content: 'Clique no nome de qualquer cliente pra ver agendamentos passados, valor gasto, preferências e observações internas.',
  },
];

const emptyForm = { name: '', phone: '', email: '', birthday: '', notes: '', tags: [] };

export default function TenantClients() {
  const confirm = useConfirm();
  const [clients,  setClients]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [page,     setPage]     = useState(1);
  const [modal,    setModal]    = useState(null); // null | 'create' | client obj | { view: client, appts }
  const [detail,   setDetail]   = useState(null);
  const [form,     setForm]     = useState(emptyForm);
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getClients({ search, page, limit: 50 })
      .then(r => { setClients(r.data.clients || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error('Erro ao carregar clientes'))
      .finally(() => setLoading(false));
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setModal('create'); };
  const openEdit   = c  => { setForm({ name: c.name, phone: c.phone || '', email: c.email || '', birthday: c.birthday?.split('T')[0] || '', notes: c.notes || '', tags: safeParseJSON(c.tags, []) }); setModal(c); };
  const openView   = async c => {
    try {
      const r = await getClientById(c.id);
      setDetail(r.data);
      setModal({ view: true });
    } catch { toast.error('Erro ao carregar cliente'); }
  };

  const onSave = async e => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Nome é obrigatório');
    setSaving(true);
    try {
      if (modal === 'create') { await createClient(form); toast.success('Cliente criado'); }
      else { await updateClient(modal.id, form); toast.success('Cliente atualizado'); }
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const onDelete = async id => {
    const ok = await confirm({
      title: 'Remover este cliente?',
      message: 'O cliente será removido do seu CRM. Agendamentos passados permanecem no histórico, mas sem o vínculo com a ficha.',
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (!ok) return;
    try { await deleteClient(id); toast.success('Cliente removido'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro ao remover'); }
  };

  const onSync = async () => {
    try { const r = await syncClientsFromAppointments(); toast.success(`${r.data.synced} clientes sincronizados`); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro ao sincronizar'); }
  };

  const fmt = formatDate;

  return (
    <div className="page-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, color: 'var(--gray-900)', fontSize: 22, fontWeight: 700 }}>Clientes / CRM</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: 13 }}>{total} cliente{total !== 1 ? 's' : ''} cadastrado{total !== 1 ? 's' : ''}</p>
          <div style={{ marginTop: 8 }}>
            <PageTour tourId="clients" steps={TOUR_STEPS} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary" data-tour="clients-sync-btn" onClick={onSync}>Sincronizar agendamentos</button>
          <button className="btn btn-primary" data-tour="clients-new-btn" onClick={openCreate}>+ Novo cliente</button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input className="input" data-tour="clients-search" placeholder="Buscar por nome, telefone ou e-mail…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ maxWidth: 360 }} />
      </div>

      <div className="table-container">
        <table className="table">
          <thead><tr>
            <th>Nome</th><th>Telefone</th><th>E-mail</th><th>Agendamentos</th><th>Último atend.</th><th></th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></td></tr>
            ) : clients.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>
                {search ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado. Clique em "Sincronizar" para importar do histórico de agendamentos.'}
              </td></tr>
            ) : clients.map(c => (
              <tr key={c.id}>
                <td data-label="Nome"><strong style={{ cursor: 'pointer', color: 'var(--primary)' }} onClick={() => openView(c)}>{c.name}</strong></td>
                <td data-label="Telefone">{formatPhoneBR(c.phone) || '—'}</td>
                <td data-label="E-mail">{c.email || '—'}</td>
                <td data-label="Agendamentos">{c.total_appointments || 0}</td>
                <td data-label="Último atend.">{c.last_appointment ? fmt(c.last_appointment) : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(c)}>Editar</button>
                    <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => onDelete(c.id)}>Remover</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form modal */}
      {(modal === 'create' || (modal && !modal.view)) && modal !== null && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{modal === 'create' ? 'Novo cliente' : 'Editar cliente'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={onSave}>
              <div className="modal-body">
                <div className="form-group"><label className="label">Nome *</label>
                  <input className="input" name="name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-group"><label className="label">Telefone</label>
                  <PhoneInput
                    value={form.phone}
                    onChange={(masked, normalized) => setForm(f => ({ ...f, phone: normalized || masked }))}
                  />
                </div>
                <div className="form-group"><label className="label">E-mail</label>
                  <input className="input" type="email" name="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div className="form-group"><label className="label">Aniversário</label>
                  <input className="input" type="date" name="birthday" value={form.birthday} onChange={e => setForm(f => ({ ...f, birthday: e.target.value }))} />
                </div>
                <div className="form-group"><label className="label">Observações</label>
                  <textarea className="input" name="notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>
                  {saving ? '' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {modal && modal.view && detail && (
        <div className="modal-overlay" onClick={() => { setModal(null); setDetail(null); }}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{detail.client.name}</h3>
              <button className="modal-close" onClick={() => { setModal(null); setDetail(null); }}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div><div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>TELEFONE</div><div style={{ fontWeight: 600 }}>{formatPhoneBR(detail.client.phone) || '—'}</div></div>
                <div><div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>E-MAIL</div><div style={{ fontWeight: 600 }}>{detail.client.email || '—'}</div></div>
                <div><div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>ANIVERSÁRIO</div><div style={{ fontWeight: 600 }}>{detail.client.birthday ? fmt(detail.client.birthday) : '—'}</div></div>
              </div>
              {detail.client.notes && <div style={{ background: 'var(--gray-50)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 14, color: 'var(--gray-700)' }}>{detail.client.notes}</div>}
              <h4 style={{ margin: '0 0 8px', fontSize: 14, color: 'var(--gray-700)' }}>Histórico de agendamentos</h4>
              {detail.appointments.length === 0 ? (
                <p style={{ color: 'var(--gray-400)', fontSize: 13 }}>Nenhum agendamento encontrado.</p>
              ) : detail.appointments.map(a => (
                <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 13 }}>
                  <span>{a.service_name || 'Serviço'}</span>
                  <span style={{ color: 'var(--gray-500)' }}>{fmt(a.datetime)}</span>
                  <span className={`badge badge-${a.status === 'scheduled' ? 'info' : a.status === 'completed' ? 'success' : 'error'}`}>{a.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
